// Tarjetas de información de ofertas (read-only)

import React from 'react';
import { Trophy, AlertTriangle, Lock, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * Tarjeta: Oferta más alta / Precio de salida
 * Muestra el precio actual de la propiedad (oferta más alta o precio inicial)
 *
 * @param {boolean} hasBids - Si hay ofertas
 * @param {number} currentPrice - Precio actual
 * @param {object} highestBid - Oferta más alta { buyerName, amount, ... }
 */
export const HighestBidCard = ({ hasBids, currentPrice, highestBid }) => {
  const { t, i18n } = useTranslation();
  return (
    <div className={`backdrop-blur-xl rounded-xl p-3 border min-h-[82px] relative ${
      hasBids
        ? 'bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/20'
        : 'bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/20'
    }`}>
      <div className={`text-xs font-medium mb-1 ${hasBids ? 'text-amber-400' : 'text-emerald-400'}`}>
        {hasBids ? t('bidCards.highestBid') : t('bidCards.salePrice')}
      </div>
      <div className={`text-2xl md:text-3xl font-bold ${hasBids ? 'text-white' : 'text-emerald-300'}`}>
        {formatCurrency(currentPrice, i18n.language)}
      </div>
      {hasBids && (
        <div className="absolute top-3 right-3 text-xs text-slate-400">
          {highestBid.buyerName.charAt(0)}.
        </div>
      )}
    </div>
  );
};

/**
 * Tarjeta: Tu última oferta
 * Muestra el estado de la última oferta del usuario
 *
 * @param {boolean} isTopBidder - Si el usuario es el ganador actual
 * @param {object} userLastBid - Última oferta { amount, timestamp, selectedConditions, conditionsText, ... }
 * @param {number} currentPrice - Precio actual (para mostrar diferencia)
 * @param {function} getTimeSince - Función para formatear tiempo transcurrido
 * @param {boolean} isClosed - Si la propiedad está cerrada
 * @param {boolean} isLastChance - Si está en periodo de última oportunidad
 */
export const UserLastBidCard = ({
  isTopBidder,
  userLastBid,
  currentPrice,
  getTimeSince,
  isClosed = false,
  isLastChance = false
}) => {
  const { t, i18n } = useTranslation();
  const hasConditions = userLastBid.selectedConditions?.length > 0 || userLastBid.conditionsText;

  return (
    <div className={`backdrop-blur-xl rounded-xl p-3 border ${
      isTopBidder
        ? 'bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/20'
        : 'bg-gradient-to-br from-red-900/20 to-red-800/20 border-red-500/30'
    }`}>
      <div className="flex items-center gap-1.5 mb-1">
        {isTopBidder ? (
          <Trophy className="w-3.5 h-3.5 text-green-400" />
        ) : (
          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
        )}
        <h3 className={`text-xs font-semibold ${isTopBidder ? 'text-green-400' : 'text-red-400'}`}>
          {isTopBidder
            ? (isClosed || isLastChance)
              ? t('bidCards.won')
              : t('bidCards.winning')
            : (isClosed || isLastChance)
              ? t('bidCards.yourMaxBid')
              : t('bidCards.outbid')
          }
        </h3>
      </div>
      <div className="text-xl md:text-2xl font-bold text-white">
        {formatCurrency(userLastBid.amount, i18n.language)}
      </div>
      {/* Icono de condiciones en esquina superior derecha */}
      {hasConditions && userLastBid.conditionsText && (
        <div
          className="absolute top-3 right-3 text-blue-400 cursor-help"
          title={userLastBid.conditionsText}
        >
          <FileText className="w-4 h-4" />
        </div>
      )}
    </div>
  );
};

/**
 * Tarjeta: Estado UPCOMING (bloqueado)
 * Se muestra cuando la propiedad aún no está disponible para pujar
 */
export const UpcomingBidCard = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-3 border border-slate-700/50 flex items-center gap-2 min-h-[82px]">
      <div className="bg-slate-700/50 p-2 rounded-lg flex-shrink-0">
        <Lock className="w-5 h-5 text-slate-400" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-white leading-tight mb-1">
          {t('bidCards.upcoming.title')}
        </h3>
        <p className="text-sm text-slate-400 leading-tight">
          {t('bidCards.upcoming.desc')}
        </p>
      </div>
    </div>
  );
};

/**
 * Tarjeta: Oferta ciega del usuario (cuando finalizó sin fase abierta)
 * Badge neutro que muestra "Tu oferta fue de" con el valor
 * Solo se muestra cuando la propiedad está CLOSED y no hubo fase abierta
 *
 * @param {object} userBlindOffer - Oferta ciega del usuario { amount, ... }
 */
export const UserBlindOfferCard = ({ userBlindOffer }) => {
  const { t, i18n } = useTranslation();
  if (!userBlindOffer) return null;

  return (
    <div className="backdrop-blur-xl rounded-xl p-3 border bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/20">
      <div className="flex items-center gap-1.5 mb-1">
        <Lock className="w-3.5 h-3.5 text-purple-400" />
        <h3 className="text-xs font-semibold text-purple-400">
          {t('bidCards.blindOfferWas')}
        </h3>
      </div>
      <div className="text-xl md:text-2xl font-bold text-white">
        {formatCurrency(userBlindOffer.amount, i18n.language)}
      </div>
    </div>
  );
};
